import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const ExportModal = ({ 
  isOpen = false, 
  onClose = () => {},
  onExport = () => {},
  totalMessages = 0,
  className = '' 
}) => {
  const [exportOptions, setExportOptions] = useState({
    format: 'csv',
    dateRange: 'all',
    includeFields: {
      recipient: true,
      content: true,
      timestamp: true,
      status: true,
      channel: true,
      type: true
    },
    filters: {
      status: 'all',
      channel: 'all',
      type: 'all'
    }
  });

  const [isExporting, setIsExporting] = useState(false);

  if (!isOpen) return null;

  const formatOptions = [
    { value: 'csv', label: 'CSV (.csv)' },
    { value: 'excel', label: 'Excel (.xlsx)' },
    { value: 'json', label: 'JSON (.json)' },
    { value: 'pdf', label: 'PDF Report (.pdf)' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'delivered', label: 'Delivered Only' },
    { value: 'failed', label: 'Failed Only' },
    { value: 'pending', label: 'Pending Only' }
  ];

  const channelOptions = [
    { value: 'all', label: 'All Channels' },
    { value: 'email', label: 'Email Only' },
    { value: 'sms', label: 'SMS Only' },
    { value: 'whatsapp', label: 'WhatsApp Only' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'birthday', label: 'Birthday Messages' },
    { value: 'random', label: 'Random Contact' },
    { value: 'manual', label: 'Manual Messages' }
  ];

  const handleFieldToggle = (field) => {
    setExportOptions(prev => ({
      ...prev,
      includeFields: {
        ...prev?.includeFields,
        [field]: !prev?.includeFields?.[field]
      }
    }));
  };

  const handleFilterChange = (filterType, value) => {
    setExportOptions(prev => ({
      ...prev,
      filters: {
        ...prev?.filters,
        [filterType]: value
      }
    }));
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await onExport(exportOptions);
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const getEstimatedCount = () => {
    // This would normally calculate based on filters
    return Math.floor(totalMessages * 0.8); // Mock calculation
  };

  return (
    <div className="fixed inset-0 z-500 flex items-center justify-center p-4 bg-black/50">
      <div className={`bg-surface rounded-lg shadow-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Download" size={24} className="text-primary" />
            <h2 className="text-xl font-semibold text-text-primary">
              Export Message History
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Export Format */}
          <div>
            <Select
              label="Export Format"
              description="Choose the file format for your export"
              options={formatOptions}
              value={exportOptions?.format}
              onChange={(value) => setExportOptions(prev => ({ ...prev, format: value }))}
            />
          </div>

          {/* Date Range */}
          <div>
            <Select
              label="Date Range"
              description="Select the time period for messages to export"
              options={dateRangeOptions}
              value={exportOptions?.dateRange}
              onChange={(value) => setExportOptions(prev => ({ ...prev, dateRange: value }))}
            />
          </div>

          {/* Filters */}
          <div className="space-y-4">
            <h3 className="font-medium text-text-primary">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label="Status"
                options={statusOptions}
                value={exportOptions?.filters?.status}
                onChange={(value) => handleFilterChange('status', value)}
              />
              <Select
                label="Channel"
                options={channelOptions}
                value={exportOptions?.filters?.channel}
                onChange={(value) => handleFilterChange('channel', value)}
              />
              <Select
                label="Type"
                options={typeOptions}
                value={exportOptions?.filters?.type}
                onChange={(value) => handleFilterChange('type', value)}
              />
            </div>
          </div>

          {/* Include Fields */}
          <div className="space-y-4">
            <h3 className="font-medium text-text-primary">Include Fields</h3>
            <div className="grid grid-cols-2 gap-4">
              <Checkbox
                label="Recipient Information"
                checked={exportOptions?.includeFields?.recipient}
                onChange={() => handleFieldToggle('recipient')}
              />
              <Checkbox
                label="Message Content"
                checked={exportOptions?.includeFields?.content}
                onChange={() => handleFieldToggle('content')}
              />
              <Checkbox
                label="Timestamp"
                checked={exportOptions?.includeFields?.timestamp}
                onChange={() => handleFieldToggle('timestamp')}
              />
              <Checkbox
                label="Delivery Status"
                checked={exportOptions?.includeFields?.status}
                onChange={() => handleFieldToggle('status')}
              />
              <Checkbox
                label="Channel"
                checked={exportOptions?.includeFields?.channel}
                onChange={() => handleFieldToggle('channel')}
              />
              <Checkbox
                label="Message Type"
                checked={exportOptions?.includeFields?.type}
                onChange={() => handleFieldToggle('type')}
              />
            </div>
          </div>

          {/* Export Summary */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="Info" size={16} className="text-text-secondary" />
              <span className="font-medium text-text-primary">Export Summary</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-text-secondary">Estimated messages:</span>
                <span className="font-medium text-text-primary">{getEstimatedCount()?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Format:</span>
                <span className="font-medium text-text-primary uppercase">{exportOptions?.format}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Fields included:</span>
                <span className="font-medium text-text-primary">
                  {Object.values(exportOptions?.includeFields)?.filter(Boolean)?.length} of 6
                </span>
              </div>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Shield" size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-text-primary mb-1">Privacy Notice</h4>
                <p className="text-sm text-text-secondary">
                  Exported data contains personal information. Please handle securely and in compliance with privacy regulations.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleExport}
            loading={isExporting}
            iconName="Download"
            iconPosition="left"
          >
            {isExporting ? 'Exporting...' : 'Export Messages'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;